import uniqBy from 'lodash/uniqBy';
import { MapboxGeoJSONFeature } from "mapbox-gl";

interface MapBoundingBoxProps {
  map: any;
  clusteredLayers: any;
  geoJsonFiles: any;
  onFinish: Function
}

const mapboxgl = require('mapbox-gl');

export default class MapBoundingBox {
  private map: any;
  private canvas: any;
  private box: any;
  private current: any;
  private start: any;
  private geoPoints: any;
  private mapPoints: any;
  private hiddenlayers: any = [];
  private data: MapboxGeoJSONFeature[]|undefined;
  private onFinish: Function;

  constructor(props: MapBoundingBoxProps) {
    this.map = props.map;
    this.onFinish = props.onFinish;

    this.geoPoints = [];
    this.mapPoints = [];

    this.set = this.set.bind(this);
    this.mousePos = this.mousePos.bind(this);
    this.mouseDown = this.mouseDown.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.finish = this.finish.bind(this);
  }

  public set() {
    this.map.boxZoom.disable();
    this.canvas = this.map.getCanvasContainer();

    this.setSourcesAndLayers();

    // Set `true` to dispatch the event before other functions
    // call it. This is necessary for disabling the default map
    // dragging behaviour.
    this.canvas.addEventListener('mousedown', this.mouseDown, true);

    this.map.on('mousedown', (e: any) => {
      if (this.mapPoints.length > 0) {
        this.mapPoints.forEach((p: any) => {
          p.remove();
        });
      }

      // Continue the rest of the function if the shiftkey is pressed.
      if (e.originalEvent.shiftKey && e.originalEvent.button === 0) {
        this.geoPoints.push(e.lngLat);
      } else {
        this.geoPoints = [];
      }

      const marker = this.addMarker();
      this.mapPoints.push(marker);
    });

    this.map.on('mouseup', (e: any) => {
      if (e.originalEvent.shiftKey && e.originalEvent.button === 0) {
        this.geoPoints.push(e.lngLat);
      } else {
        this.geoPoints = [];
      }
      const marker = this.addMarker();
      this.mapPoints.push(marker);
    });
  }

  setData(data: MapboxGeoJSONFeature[]|undefined) {
    this.data = data;
  }

  setSourcesAndLayers() {
    const sourceId = 'bounding-box-unclustered-source';

    if (this.map.getSource(sourceId)) {
      this.map.getSource(sourceId).setData({ type: 'FeatureCollection', features: this.data });
    } else {
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: this.data },
        cluster: false
      });
    }

    const layerId = sourceId + '-layer';

    this.map.getLayer(layerId) && this.map.removeLayer(layerId);
    this.map.addLayer({
      id: layerId,
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-radius': 0 // this makes the layer invisible
      }
    });
    this.hiddenlayers.push(layerId);
  }

  addMarker() {
    // create a HTML element for each feature
    const el = document.createElement('div');
    el.className = 'marker';

    // make a marker for each feature and add to the map
    return new mapboxgl.Marker(el);
  }

  // Return the xy coordinates of the mouse position
  mousePos(e: MouseEvent) {
    const rect = this.canvas.getBoundingClientRect();
    return new mapboxgl.Point(
      e.clientX - rect.left - this.canvas.clientLeft,
      e.clientY - rect.top - this.canvas.clientTop
    )
  }

  mouseDown(e: MouseEvent) {
    // Continue the rest of the function if the shiftkey is pressed.
    if (!(e.shiftKey && e.button === 0)) {
      return;
    }

    // Disable default drag zooming when the shift key is held down.
    this.map.dragPan.disable();

    // Call functions for the following events
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
    document.addEventListener('keydown', this.onKeyDown);

    // Capture the first xy coordinates
    this.start = this.mousePos(e);
  }

  onMouseMove(e: MouseEvent) {
    // Capture the ongoing xy coordinates
    this.current = this.mousePos(e);

    // Append the box element if it doesnt exist
    if (!this.box) {
      this.box = document.createElement('div');
      this.box.classList.add('boxdraw');
      this.canvas.appendChild(this.box);
    }

    const minX = Math.min(this.start.x, this.current.x),
    maxX = Math.max(this.start.x, this.current.x),
    minY = Math.min(this.start.y, this.current.y),
    maxY = Math.max(this.start.y, this.current.y);

    // Adjust width and xy position of the box element ongoing
    const pos = `translate(${minX}px, ${minY}px)`;
    this.box.style.transform = pos;
    this.box.style.width = maxX - minX + 'px';
    this.box.style.height = maxY - minY + 'px';
  }

  onMouseUp(e: MouseEvent) {
    // Capture xy coordinates
    this.finish([this.start, this.mousePos(e)]);
  }

  onKeyDown(e: KeyboardEvent) {
    // If the ESC key is pressed
    if (e.code == '27') {
      this.finish();
    }
  }

  finish(bbox?: any) {
    // Remove these events now that finish has been called.
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('mouseup', this.onMouseUp);

    if (this.box) {
      this.box.parentNode.removeChild(this.box);
      this.box = null;
    }
     
    // If bbox exists. use this value as the argument for `queryRenderedFeatures`
    if (bbox && this.geoPoints.length === 2) {
      this.geoPoints = this.geoPoints.sort((a: any, b: any) => {
        if (a.lat > b.lat) {
          return -1;
        }
        if (a.lat < b.lat) {
          return 1;
        }
        return 0;
      });
      const polygonFilter = ['within', {
        type: 'Polygon',
        coordinates: [
          [
            [this.geoPoints[0].lng, this.geoPoints[0].lat],
            [this.geoPoints[1].lng, this.geoPoints[0].lat],
            [this.geoPoints[1].lng, this.geoPoints[1].lat],
            [this.geoPoints[0].lng, this.geoPoints[1].lat],
          ]
        ]
      }];

      const features = uniqBy(
        this.map.queryRenderedFeatures({
          layers: this.hiddenlayers,
          filter: polygonFilter
        }),
        'properties.id'
      );

      this.onFinish(features);
    }

    this.geoPoints = [];
    this.map.dragPan.enable();
  }
}